import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { withLocalization } from 'bernie-l10n';

import { ViewLarge, Viewport } from '@shared-ui/viewport-context';

import { SearchCriteriaModel } from 'stores/models';

import { UitkLayoutGridItem } from 'uitk-react-layout-grid';

import { DateRangeProps, DateRangeState } from 'src/common/components/common/search-form/fields/date-range';
import { FormFieldBinding } from 'src/common/components/common/search-form/fields/form-field-binding';
import { NewDateField } from 'src/common/components/common/search-form/fields/new-date-field';

const logger = ClientLogger.getLoggerWithIdentifier('pwa.component.searchform');

@withLocalization
@FormFieldBinding
export class PartialStayDateRange extends React.Component<DateRangeProps, DateRangeState> {
  constructor(props: any) {
    super(props);

    this.state = {
      endDateFocus: undefined,
      startDateFocus: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.registerFocusMethod = this.registerFocusMethod.bind(this);
    this.validate = this.validate.bind(this);

    const { registerValidator } = props;

    registerValidator(() => this.validate('startDate'));
    registerValidator(() => this.validate('endDate'));
  }

  public render() {
    const { l10n, model } = this.props;
    const { formatText } = l10n;

    const validPartialStart = model.partialStayStartDate && this.isValidDate(new Date(model.partialStayStartDate));
    const validPartialEnd = model.partialStayEndDate && this.isValidDate(new Date(model.partialStayEndDate));

    const startDate = validPartialStart ? model.partialStayStartDate : model.startDate;
    const endDate = validPartialEnd ? model.partialStayEndDate : model.endDate;

    const buttonText = formatText('hotels.roomPicker.doneButton.label');

    const startDateError = this.validate('startDate');
    const endDateError = this.validate('endDate');

    const commonDateFieldProps = {
      startDate,
      endDate,
      onChange: this.handleChange,
      placeholder: formatText('hotels.search.form.toDate.placeholder'),
      numDaysBookableInAdvance: SearchCriteriaModel.NUM_OF_DAYS_BOOKABLE_IN_ADVANCE,
      midnightBookingOffsetHours: SearchCriteriaModel.MIDNIGHT_BOOKING_OFFSET_IN_HOURS,
      buttonText,
      registerFocusMethod: this.registerFocusMethod,
      isDateless: false,
      updateModels: this.updateModels.bind(this),
    };

    if (logger && typeof logger.logEvent === 'function' && (startDateError || endDateError) && startDate && endDate) {
      this.logDateRangeError(startDateError, endDateError, startDate, endDate);
    }

    const routerLinkWrapper = (children: any) => <>{children}</>;

    const startDateLabel = formatText('hotels.search.form.fromDateHyphen.label');
    const endDateLabel = formatText('hotels.search.form.toDateHyphen.label');

    const dateFieldProps = {
      endId: 'hotels-partial-stay-check-out',
      endInvalid: endDateError && endDateError.text,
      endLabel: endDateLabel,
      endName: 'partialStayEndDate',
      endPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
      onChange: commonDateFieldProps.onChange,
      routerLinkWrapper,
      startId: 'hotels-partial-stay-check-in',
      startInvalid: startDateError && startDateError.text,
      startLabel: startDateLabel,
      startName: 'partialStayStartDate',
      startPlaceholder: formatText('hotels.search.form.toDate.placeholder'),
    };

    const rfrr = {
      end: ':ChangePartialDatesCheckOut',
      start: ':ChangePartialDatesCheckIn',
    };

    return (
      model.isPartialStay && (
        <>
          <UitkLayoutGridItem
            colSpan={{
              small: 1,
              medium: 2,
            }}
          >
            <div>
              <NewDateField rfrr={rfrr} dateFieldProps={dateFieldProps} {...commonDateFieldProps} />
            </div>
          </UitkLayoutGridItem>
          <Viewport>
            <ViewLarge>
              <UitkLayoutGridItem>
                <div />
              </UitkLayoutGridItem>
            </ViewLarge>
          </Viewport>
        </>
      )
    );
  }

  private updateModels(startDate: string, endDate: string) {
    this.handleChange({
      target: {
        name: 'partialStayStartDate',
        value: startDate,
      },
    });

    this.handleChange({
      target: {
        name: 'partialStayEndDate',
        value: endDate,
      },
    });
  }

  private handleChange(event: any) {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel(event);
    }
  }

  private logDateRangeError(
    startDateError: { text: any; focus: (() => void) | undefined } | undefined,
    endDateError: { text: any; focus: (() => void) | undefined } | undefined,
    startDate: string,
    endDate: string
  ) {
    logger.logEvent(
      new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM__PARTIAL_STAY_DATE_RANGE_ERROR'),
      `${startDateError} | ${endDateError}`,
      `partialStayStartDate=${startDate} partialStayEndDate=${endDate}`
    );
  }

  private registerFocusMethod(fieldName: string, focusMethod: () => void) {
    this.setState({ [`${fieldName}Focus`]: focusMethod });
  }

  // eslint-disable-next-line complexity
  private validate(key: string) {
    const { l10n, model } = this.props;
    const { startDateFocus, endDateFocus } = this.state;

    const { formatText } = l10n;

    const { partialStayStartDate, partialStayEndDate, startDate, endDate, isPartialStay } = model;
    // Dont validate if partial stay isnt selected
    if (!isPartialStay) {
      return;
    }

    const start = partialStayStartDate ? partialStayStartDate : startDate;
    const end = partialStayEndDate ? partialStayEndDate : startDate;

    if (key === 'startDate' && start && startDate) {
      const partialStart = new Date(start);
      const searchStartDate = new Date(startDate);
      const message = 'hotels.search.validation.invalidFromDate.text';

      if (!this.isValidDate(partialStart) && !model.isDateless) {
        return {
          text: formatText(message),
          focus: startDateFocus,
        };
      }

      searchStartDate.setHours(0, 0, 0, 0);
      partialStart.setHours(0, 0, 0, 0);

      if (partialStart < searchStartDate) {
        return {
          text: formatText('hotels.search.validation.invalidPartialDates.text'),
          focus: endDateFocus,
        };
      }
    }

    if (key === 'endDate' && start && end && endDate) {
      const searchEndDate = new Date(endDate);
      const partialStart = new Date(start);
      const partialEnd = new Date(end);
      const message = 'hotels.search.validation.invalidToDate.text';

      if (!this.isValidDate(partialEnd) && !model.isDateless) {
        return {
          text: formatText(message),
          focus: endDateFocus,
        };
      }

      if (this.isValidDate(partialStart)) {
        if (partialEnd < partialStart) {
          return {
            text: formatText('hotels.search.validation.invalidDateRange.text'),
            focus: endDateFocus,
          };
        }

        searchEndDate.setHours(0, 0, 0, 0);
        partialEnd.setHours(0, 0, 0, 0);
        partialStart.setHours(0, 0, 0, 0);

        if (partialEnd > searchEndDate) {
          return {
            text: formatText('hotels.search.validation.invalidPartialDates.text'),
            focus: endDateFocus,
          };
        }
      }
    }
  }

  private isValidDate(date: Date | string) {
    return date && date.toString() !== 'Invalid Date';
  }
}
